import { useId } from "react";
import {
	type Control,
	Controller,
	type FieldPath,
	type FieldValues,
	useFormContext,
} from "react-hook-form";

import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";

type Props<TFormValues extends FieldValues> = {
	control?: Control<TFormValues>;
	name: FieldPath<TFormValues>;
	label?: string;
	type?: React.HTMLInputTypeAttribute;
	placeholder?: string;
	disabled?: boolean;
	autoComplete?: string;
	maxLength?: number;
	className?: string;
	description?: string;
};

export const FormInput = <TFormValues extends FieldValues>(
	props: Props<TFormValues>,
) => {
	const ctx = useFormContext<TFormValues>();
	const control = props.control ?? ctx.control;
	const id = useId();
	const {
		name,
		label,
		type = "text",
		placeholder,
		disabled,
		autoComplete,
		maxLength,
		className,
		description,
	} = props;

	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<Field
					className={className}
					data-invalid={fieldState.error ? true : undefined}
				>
					{label ? <FieldLabel htmlFor={id}>{label}</FieldLabel> : null}
					<Input
						{...field}
						id={id}
						value={field.value ?? ""}
						type={type}
						placeholder={placeholder}
						disabled={disabled}
						autoComplete={autoComplete}
						maxLength={maxLength}
						aria-invalid={!!fieldState.error}
					/>
					{description ? (
						<FieldDescription>{description}</FieldDescription>
					) : null}
					<FieldError
						errors={fieldState.error ? [fieldState.error] : undefined}
					/>
				</Field>
			)}
		/>
	);
};
